import React, { useMemo, useContext } from "react";
import fp from "lodash/fp";
import { Row, Col, Form } from "react-bootstrap";
import Creatable from "react-select/creatable";

import { Skeleton } from "components";
import { ApplicationEntryProgressContext } from "components/contexts";
import { DebounceTimeout } from "globalConstants";

import SelectAsync from "../selectAsync";
import { selectStyles } from "../selectStyles";

const SelectAsyncForBankName = ({
	input,
	meta: { touched, error, warning },
	loadOptions,
	findOption,
	onChangeCb,
	disabled,
	blurLoading,
	dropDownHeight = false,
	hideRequiredMark, //works only with RequiredMarkContext
	readOnly,
	label = "Bank",
	...props
}) => {
	const showError = touched && !disabled && !blurLoading && error;
	const { triggerSave } = useContext(ApplicationEntryProgressContext);

	const errorText = showError && (
		<p style={selectStyles.selectAsyncErrorMessage()}>{error}</p>
	);

	const showWarning = !disabled && !blurLoading && warning;

	const warningText = showWarning && (
		<p style={selectStyles.selectAsyncWarningMessage()}>{warning}</p>
	);

	const formatCreateLabel = value => `Enter "${value}"`;

	const filterOption = (option, query) => {
		if (option.data.__isNew__) return true;

		return (
			option.data.value &&
			option.data.value.toLowerCase().includes(query.toLowerCase())
		);
	};

	const handleChange = option => {
		const value = option ? option.value : "";
		input.onChange(value);
		if (onChangeCb) onChangeCb(value);
	};

	const handleBlur = () => {
		input.onBlur(input.value);
		triggerSave?.();
	};

	const SelectWithError = (
		<>
			<SelectAsync
				{...props}
				SelectComponent={Creatable}
				value={findOption(input.value)}
				onChange={handleChange}
				onBlur={handleBlur}
				formatCreateLabel={formatCreateLabel}
				filterOption={filterOption}
				debounceTimeout={DebounceTimeout}
				cacheOptions
				loadOptions={loadOptions}
				dropDownHeight={dropDownHeight}
				additional={{ page: 0 }}
				isClearable={true}
				maxMenuHeight={160}
			/>
			{warningText || errorText}
		</>
	);

	const SelectWithLoader = useMemo(
		() =>
			fp.cond([
				[() => blurLoading, () => <Skeleton />],
				[
					() => readOnly,
					({ input }) => (
						<div className={"form-control-plaintext"}>
							{input.value}
						</div>
					),
				],
				[fp.T, ({ component }) => component],
			]),
		[blurLoading, readOnly],
	);

	const isDisabledCss = readOnly && disabled && { opacity: "0.1" };

	return (
		<Row style={{ marginBottom: 22, ...isDisabledCss }}>
			<Form.Label column sm={6}>
				{label}
			</Form.Label>
			<Col sm={6}>
				<SelectWithLoader input={input} component={SelectWithError} />
			</Col>
		</Row>
	);
};

export default SelectAsyncForBankName;
